import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'

import {
  getFirstActiveDayWorkingHours,
  hasOnlyOneActiveDay,
  areWeeklyWorkingHoursRepeated
} from 'pages/settings/components/workinghours/utils.js'
import { constants, utils } from 'gipsy-misc'
import { user } from 'gipsy-api'

import unset from 'lodash/unset'
import cloneDeep from 'lodash/cloneDeep'

const propTypes = {
  weeklyWorkingHours: PropTypes.object,
  showErrorSnackbar: PropTypes.func.isRequired,
  setWeeklyWorkingHours: PropTypes.func.isRequired,
  handleAPIError: PropTypes.func.isRequired
}

const DELAY_BEFORE_PATCH_REQUEST = 500

export default function wrapper(Component) {
  class WorkingHoursContainer extends PureComponent {
    state = {
      saved: false,
      weeklyWorkingHoursRepeated: true,
      weeklyWorkingHoursRepeatedSet: false
    }

    componentDidMount = () => {
      const weeklyWorkingHours = this.props.weeklyWorkingHours
      this.setState({
        weeklyWorkingHoursRepeated: areWeeklyWorkingHoursRepeated(weeklyWorkingHours),
        // if the list is opened by default, we don't want to close it when the weeklyWorkingHours becomes identical
        weeklyWorkingHoursRepeatedSet: !areWeeklyWorkingHoursRepeated(weeklyWorkingHours)
      })
    }

    componentWillUnmount() {
      clearTimeout(this.timeout)
    }

    toggleWorkingDay = payload => {
      let weeklyWorkingHours = cloneDeep(this.props.weeklyWorkingHours)
      if (weeklyWorkingHours[payload.day].active && hasOnlyOneActiveDay(weeklyWorkingHours)) {
        this.props.showErrorSnackbar('message.error.workingHours.invalidActiveWeekdays')
        return
      }
      weeklyWorkingHours = this._toggleWorkingDay(payload)

      utils.resolve.once(
        'workingHours',
        () => {
          this.updateWorkingHours(weeklyWorkingHours)
        },
        DELAY_BEFORE_PATCH_REQUEST
      )
    }

    _toggleWorkingDay = payload => {
      const weeklyWorkingHours = cloneDeep(this.props.weeklyWorkingHours)
      const areWeeklyWorkingHoursRepeated = this.state.areWeeklyWorkingHoursRepeated
      weeklyWorkingHours[payload.day].active = !weeklyWorkingHours[payload.day].active
      if (weeklyWorkingHours[payload.day].active) {
        const firstActiveDayWorkingHours = getFirstActiveDayWorkingHours(weeklyWorkingHours)
        /* assign first active day workhours if repeat is true or default workhours (9am - 6pm) */
        let workStart =
          areWeeklyWorkingHoursRepeated && firstActiveDayWorkingHours
            ? firstActiveDayWorkingHours.workStart
            : constants.defaultWorkStart
        let workEnd =
          areWeeklyWorkingHoursRepeated && firstActiveDayWorkingHours
            ? firstActiveDayWorkingHours.workEnd
            : constants.defaultWorkEnd
        weeklyWorkingHours[payload.day].workStart = workStart
        weeklyWorkingHours[payload.day].workEnd = workEnd
      } else {
        unset(weeklyWorkingHours, `${payload.day}.workStart`)
        unset(weeklyWorkingHours, `${payload.day}.workEnd`)
      }
      return weeklyWorkingHours
    }

    updateWorkingHours = async weeklyWorkingHours => {
      this.props.setWeeklyWorkingHours(weeklyWorkingHours)
      try {
        await user.patch(weeklyWorkingHours, { weeklyWorkingHours: true })
        this.setSave()
      } catch (err) {
        this.props.handleAPIError(err)
      }
    }

    setSave = () => {
      this.setState({ saved: true })
      this.timeout = setTimeout(() => {
        this.setState({ saved: false })
      }, 5000)
    }

    onChangeWorkHours = (type, { paramName, value }) => {
      let workHours = {
        [type]: {
          hour: value.hour,
          minute: value.minute
        }
      }
      this.setDayWorkHours({
        day: paramName,
        ...workHours
      })
    }

    onChangeRepeat = ({ paramName, value }) => {
      const weeklyWorkingHours = cloneDeep(this.props.weeklyWorkingHours)
      const firstActiveDayWorkingHours = getFirstActiveDayWorkingHours(weeklyWorkingHours)

      this.setState({ weeklyWorkingHoursRepeated: value, weeklyWorkingHoursRepeatedSet: true })

      if (!value) {
        return
      }
      this.setDayWorkHours({
        day: paramName,
        workStart: firstActiveDayWorkingHours.workStart,
        workEnd: firstActiveDayWorkingHours.workEnd
      })
    }

    setDayWorkHours = payload => {
      let workingHours
      if (payload.day === 'all') {
        workingHours = this._setAllDayWorkHours(payload)
      } else {
        workingHours = this._setDayWorkHours(payload)
      }

      utils.resolve.once(
        'workingHours',
        () => {
          this.updateWorkingHours(workingHours)
        },
        DELAY_BEFORE_PATCH_REQUEST
      )
    }

    _setAllDayWorkHours = payload => {
      const weeklyWorkingHours = cloneDeep(this.props.weeklyWorkingHours)
      for (let day in weeklyWorkingHours) {
        if (weeklyWorkingHours[day].active) {
          if (payload.workStart) {
            weeklyWorkingHours[day].workStart = payload.workStart
          }
          if (payload.workEnd) {
            weeklyWorkingHours[day].workEnd = payload.workEnd
          }
        }
      }

      return weeklyWorkingHours
    }

    _setDayWorkHours = payload => {
      const weeklyWorkingHours = cloneDeep(this.props.weeklyWorkingHours)
      weeklyWorkingHours[payload.day] = { ...weeklyWorkingHours[payload.day] }
      if (payload.workStart) {
        weeklyWorkingHours[payload.day].workStart = payload.workStart
      }
      if (payload.workEnd) {
        weeklyWorkingHours[payload.day].workEnd = payload.workEnd
      }

      return weeklyWorkingHours
    }

    deepCloneWorkingHours = weeklyWorkingHours => {
      if (!weeklyWorkingHours || !Object.keys(weeklyWorkingHours).length) {
        return undefined
      }
      const newWeeklWorkingHours = {}
      for (let k of Object.keys(weeklyWorkingHours)) {
        const day = cloneDeep(weeklyWorkingHours[k])
        if (day.workStart) {
          day.workStart = { ...this.props.weeklyWorkingHours[k].workStart }
        }
        if (day.workEnd) {
          day.workEnd = { ...this.props.weeklyWorkingHours[k].workEnd }
        }
        newWeeklWorkingHours[k] = day
      }
      return newWeeklWorkingHours
    }

    render() {
      const { weeklyWorkingHoursRepeated } = this.state
      const { weeklyWorkingHours } = this.props

      return (
        <Component
          weeklyWorkingHours={weeklyWorkingHours}
          onChangeWorkHours={this.onChangeWorkHours}
          onChangeRepeat={this.onChangeRepeat}
          toggleWorkingDay={this.toggleWorkingDay}
          firstActiveDayWorkingHours={getFirstActiveDayWorkingHours(weeklyWorkingHours)}
          saved={this.state.saved}
          areWeeklyWorkingHoursRepeated={weeklyWorkingHoursRepeated}
        />
      )
    }
  }

  WorkingHoursContainer.propTypes = propTypes

  return WorkingHoursContainer
}



// WEBPACK FOOTER //
// ./src/pages/settings/components/workinghours/container.js